import { inject, Injectable } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { RecaptchaService } from '@services/recaptcha-service';
import { TurnstileService } from '@services/turnstile-service';

export interface OtpChallengeParams {
  recaptcha?: { action: string; token: string };
  turnstile?: { token: string };
  // Index signature so this typed bag is assignable to the connection-service
  // `Record<string, unknown>` OTP params (a typed variable, unlike an object literal,
  // otherwise isn't index-compatible).
  [key: string]: unknown;
}

/** The subset of the server Config we read to decide which bot-protection challenges are active. */
interface BotProtectionConfig {
  get(key: 'disableRecaptcha' | 'disableTurnstile'): boolean | undefined;
}

/**
 * Resolves the bot-protection tokens to attach to an OTP request, honouring the server Config
 * flags `disableRecaptcha` and `disableTurnstile`. This lets ops switch reCAPTCHA off and
 * Turnstile on (or run both) without shipping an app release.
 *
 * When the config fetch fails we default to treating both as enabled — the safe superset, since
 * the server silently skips whichever challenge it has disabled but hard-requires reCAPTCHA when
 * it is enabled.
 */
@Injectable({ providedIn: 'root' })
export class BotProtectionService {
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly recaptcha: RecaptchaService = inject(RecaptchaService);
  private readonly turnstile: TurnstileService = inject(TurnstileService);

  /**
   * @param action           challenge action name (e.g. 'requestOTP', 'retryOTP').
   * @param includeRecaptcha whether reCAPTCHA applies to this flow. The server's OTP-retry path
   *                         only verifies Turnstile, so the resend flow passes `false`.
   */
  async getOtpChallengeParams(action: string, includeRecaptcha: boolean = true): Promise<OtpChallengeParams> {
    const config: BotProtectionConfig | null = await this.conn.fetchConfig();
    const params: OtpChallengeParams = {};

    if (includeRecaptcha && !config?.get('disableRecaptcha')) {
      params.recaptcha = { action, token: await this.recaptcha.execute(action) };
    }
    if (!config?.get('disableTurnstile')) {
      const turnstileToken: string | null = await this.turnstile.execute(action);
      if (turnstileToken) params.turnstile = { token: turnstileToken };
    }
    return params;
  }
}
